import React, { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, fmtDate, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, FileText, ShoppingCart, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { logError } from "@/lib/log";
import CardknoxPayDialog from "@/components/CardknoxPayDialog";

const STATUS_PILL = (s) => ({
  draft_quote: "bg-secondary text-foreground",
  quote_sent: "bg-primary/10 text-primary",
  placed: "bg-amber-100 text-amber-800",
  paid: "bg-amber-100 text-amber-800",
  fulfilled: "bg-success/10 text-success",
  declined: "bg-destructive/10 text-destructive",
  cancelled: "bg-muted text-muted-foreground",
}[s] || "bg-muted text-muted-foreground");

const STATUS_LABEL = (s) => ({
  draft_quote: "Draft",
  quote_sent: "Awaiting your acceptance",
  placed: "Payment requested",
  paid: "Preparing",
  fulfilled: "Complete",
  declined: "Declined",
  cancelled: "Cancelled",
}[s] || s.replace("_", " "));

/**
 * Customer-portal Orders & Quotes panel.
 * Shows two slices of the customer's recent orders so they can:
 *   1. Accept a quote with a single click
 *   2. See their pending payment-requested orders
 *
 * Older fulfilled/declined orders are intentionally hidden from this lean view.
 */
async function acceptQuoteApi(orderId) {
  return (await api.post(`/portal/quotes/${orderId}/accept`)).data;
}

export default function PortalOrdersList() {
  const [orders, setOrders] = useState([]);
  const [payOrder, setPayOrder] = useState(null);
  const [cof, setCof] = useState(null);
  const [chargingId, setChargingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, meR] = await Promise.all([
        api.get("/portal/orders"),
        api.get("/portal/me").catch(() => ({ data: {} })),
      ]);
      setOrders(r.data || []);
      setCof((meR.data?.customer || {}).card_on_file || null);
    } catch (e) { logError("portal orders load", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const payWithCardOnFile = useCallback(async (o) => {
    if (!cof?.token) return;
    if (!window.confirm(`Pay ${fmtMoney(o.total)} for order ${o.number} with card ending in ${cof.last4 || "????"}?`)) return;
    setChargingId(o.id);
    try {
      await api.post(`/payments/cardknox/charge-order-saved`, { order_id: o.id });
      toast.success(`Order ${o.number} paid.`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setChargingId(null); }
  }, [cof, load]);

  const acceptQuote = useCallback((o) => {
    acceptQuoteApi(o.id).then((data) => {
      toast.success(`Quote accepted — order ${data.number} placed`);
      load();
    }).catch((e) => toast.error(formatErr(e.response?.data?.detail) || e.message));
  }, [load]);

  const activeQuotes = orders.filter((o) => o.type === "quote" && o.status === "quote_sent");
  // Show only in-flight orders here. Once an order is fulfilled the invoice
  // takes over in the customer's Invoices tab — keeps this list focused on
  // anything that still needs the customer's attention.
  const visibleOrders = orders.filter((o) => o.type === "order" && (o.status === "placed" || o.status === "paid"));

  if (activeQuotes.length === 0 && visibleOrders.length === 0) return null;

  return (
    <section className="space-y-4" data-testid="portal-orders-section">
      {activeQuotes.length > 0 && (
        <div className="border border-primary/40 rounded-sm overflow-hidden">
          <div className="flex items-center gap-2 px-6 py-4 border-b border-primary/30 bg-primary/5">
            <FileText className="w-4 h-4 text-primary" />
            <h2 className="font-display text-xl font-bold tracking-tight">Quotes for you</h2>
          </div>
          <div className="divide-y divide-border">
            {activeQuotes.map((q) => (
              <div key={q.id} className="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`portal-quote-${q.number}`}>
                <div>
                  <div className="font-mono font-bold">{q.number}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(q.created_at?.slice(0, 10))} · {q.items?.length || 0} item{q.items?.length === 1 ? "" : "s"}</div>
                </div>
                <div className="text-2xl font-display font-black tracking-tighter mono-num">{fmtMoney(q.total)}</div>
                <Button onClick={() => acceptQuote(q)} className="rounded-sm" data-testid={`portal-accept-quote-${q.number}`}>
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Accept quote
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {visibleOrders.length > 0 && (
        <div className="border border-border rounded-sm overflow-hidden">
          <div className="flex items-center gap-2 px-6 py-4 border-b border-border">
            <ShoppingCart className="w-4 h-4 text-primary" />
            <h2 className="font-display text-xl font-bold tracking-tight">Your orders</h2>
          </div>
          <div className="divide-y divide-border">
            {visibleOrders.map((o) => (
              <div key={o.id} className="px-6 py-4 flex items-center justify-between gap-3 text-sm" data-testid={`portal-order-${o.number}`}>
                <div className="flex-1">
                  <div className="font-mono font-semibold">{o.number}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(o.created_at?.slice(0, 10))} · {o.items?.length || 0} item{o.items?.length === 1 ? "" : "s"}</div>
                </div>
                <div className="mono-num font-semibold">{fmtMoney(o.total)}</div>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${STATUS_PILL(o.status)}`} data-testid={`portal-order-status-${o.number}`}>
                  {STATUS_LABEL(o.status)}
                </span>
                {o.status === "placed" && (
                  <div className="flex items-center gap-2">
                    {cof?.token && (
                      <Button
                        onClick={() => payWithCardOnFile(o)}
                        disabled={chargingId === o.id}
                        size="sm"
                        className="rounded-sm"
                        data-testid={`portal-pay-order-cof-${o.number}`}
                      >
                        {chargingId === o.id
                          ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Charging…</>)
                          : (<><CreditCard className="w-4 h-4 mr-2" /> Pay with •••• {cof.last4 || "????"}</>)}
                      </Button>
                    )}
                    <Button
                      onClick={() => setPayOrder(o)}
                      variant={cof?.token ? "outline" : "default"}
                      size="sm"
                      className="rounded-sm"
                      data-testid={`portal-pay-order-${o.number}`}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {cof?.token ? "Different card" : `Pay ${fmtMoney(o.total)}`}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <CardknoxPayDialog
        open={!!payOrder}
        onClose={() => setPayOrder(null)}
        invoice={payOrder || {}}
        mode="pay-order"
        onPaid={() => { setPayOrder(null); load(); }}
      />
    </section>
  );
}
